import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import useQueryParams from 'hooks/useQueryParams';
import SearchIcon from 'icons/Search.icon';
import { Input } from 'ncoded-component-library';
import { useLocation } from 'react-router-dom';
import { SEARCH_ROUTE } from 'data/global';

import './SearchForm.styles.scss';
import './SearchForm.styles.responsive.scss';

type SearchFormProps = {
  className?: string;
  onClose?: () => void;
};

const SearchForm: FC<SearchFormProps> = ({ className, onClose }) => {
  const {
    params: { searchString },
    setQueryParam,
    setParamsAndNavigate,
    removeAllQueryParams,
  } = useQueryParams();

  const [inputSearchValue, setInputSearchValue] = useState(searchString || '');

  const { t } = useTranslation();

  const { pathname } = useLocation();

  const currentRouteIsSearch = useMemo(
    () => pathname === SEARCH_ROUTE,
    [pathname],
  );

  const searchBy = useCallback(
    (searchString: string) => {
      removeAllQueryParams();

      if (currentRouteIsSearch) {
        setQueryParam('searchString', searchString);
      } else {
        setParamsAndNavigate(SEARCH_ROUTE, { searchString });
      }
    },
    [
      currentRouteIsSearch,
      removeAllQueryParams,
      setParamsAndNavigate,
      setQueryParam,
    ],
  );

  const onSearchSubmit = useCallback(
    (ev) => {
      ev.preventDefault();

      const formData = new FormData(ev.target);
      const searchString = formData.get('search')?.toString() || '';

      searchBy(searchString);
      onClose?.();
    },
    [onClose, searchBy],
  );

  const onSearchReset = useCallback(() => {
    setInputSearchValue('');
    searchBy('');
  }, [searchBy]);

  useEffect(() => {
    setInputSearchValue(searchString || '');
  }, [pathname, searchString]);

  return (
    <div className={classNames('search-form', className)}>
      <form
        onSubmit={onSearchSubmit}
        onReset={onSearchReset}
        onClick={(e) => e.stopPropagation()}
      >
        <Input
          name="search"
          prefixNode={<SearchIcon />}
          placeholder={t('search')}
          value={inputSearchValue}
          onChange={(ev) => setInputSearchValue(ev.target.value)}
        />
      </form>
    </div>
  );
};

export default SearchForm;
